import { DeckGL } from '@deck.gl/react';
import { HexAbandoned } from "./external/HexAbandoned";
import { H3HexagonLayer, HeatmapLayer, ScatterplotLayer } from "deck.gl";
import { useEffect, useState } from "react";
import { Map } from 'react-map-gl/mapbox';
import { useLoaderData } from "react-router-dom";
import { AbandonedVehicleLoaderData } from "./loaders/abandonedVehicleLoader";
import { AbandonedVehicle, AerialImage } from "./api/Application";
import { pixelToLatLng } from "./common/Geography";
import { AbandonedVehicleStatus } from "./api/Network";
import { fileUrl } from "./api/Url";

export interface HexMini { hex: string, count: number };

async function fetchHexAbandoned(): Promise<[HexMini[], number, number]> {
    const response = await fetch( fileUrl( 'external/hex-abandoned.json' ) );
    if( !response.ok ) {
        throw new Error( `Failed to fetch hex data: ${response.status} ${response.statusText}` );
    } 
    const data: HexAbandoned = await response.json();    
    const features = data.features.map( x => {
        const newLocal: HexMini = { hex: x.properties.grid_id, count: x.properties.count_ };
        return newLocal;
    } );
    const min = data.features.map( x => x.properties.count_ ).reduce( (a, b) => Math.min( a, b ) )
    const max = data.features.map( x => x.properties.count_ ).reduce( (a, b) => Math.max( a, b ) )
    return [features, min, max];
} 

interface TileIndex {
    x: number;
    y: number;
    z: number;
}

function customToSlippy( xCustom: number, yCustom: number, zCustom: number, maxZoom: number ): TileIndex {
    const zSlippy = maxZoom + zCustom; // adjust zoom
    const xSlippy = xCustom;
    const ySlippy = yCustom;
    return { x: xSlippy, y: ySlippy, z: zSlippy };
}

export function HexagonVisualizationPage() {
    const { imageA, imageB, vehicles } = useLoaderData() as AbandonedVehicleLoaderData;
    const [res, setRes] = useState<[HexMini[], number, number]>();
    const [data, min, max] = res ?? [];

    useEffect( () => {
        const fetchData = async () => {
            const res = await fetchHexAbandoned();
            setRes( res );            
        }
        fetchData();
    }, [] );

    const getTileBounds = (x: number, y: number, z: number): [number, number, number, number] => {
        const tileSize = 512;
        const scale = Math.pow(2, -z); // For negative zooms: z=-3 → scale=8, z=0 → scale=1
        
        // Calculate pixel coordinates for this tile
        const pixelLeft = x * tileSize * scale;
        const pixelTop = y * tileSize * scale;
        const pixelRight = (x + 1) * tileSize * scale;
        const pixelBottom = (y + 1) * tileSize * scale;
        
        // Convert to lat/lng using your existing conversion
        const [west, north] = pixelToLatLng(pixelLeft, pixelTop, imageB);
        const [east, south] = pixelToLatLng(pixelRight, pixelBottom, imageB);
        
        return [west, south, east, north];
    };    

    if( !data || !min || !max ) {
        return <></>;
    }

    const h3Layer = new H3HexagonLayer<HexMini>( {
        id:       'hex-layer',
        data:     data,
        opacity:  1.0,
        getHexagon: ( h: HexMini ) => h.hex,
        getElevation: 0,
        getFillColor: ( h: HexMini ) => {
            const nonRed = 255 * ( 1 - ( h.count / max ) );
            return [255, nonRed, nonRed, 200];
        },
        material: {
            ambient: 0.64,
            diffuse: 0.6,
            shininess: 32,
            specularColor: [51, 51, 51]
        },
        getLineColor: [0, 0, 0, 255],
        pickable: true,
    } );

    const scatterLayer = new ScatterplotLayer( {
        id: 'green-dots-layer',
        data: vehicles.map( v => {
            const ret = pixelToLatLng( v.x, v.y, imageB );
            return { position: ret, status: v.status.status };
        } ),
        getPosition: d => {
            return d.position;
        },
        getFillColor: d => {
            switch( d.status ) {
                case AbandonedVehicleStatus.Candidate:        return [255, 255,   0,   0]; //yellow
                case AbandonedVehicleStatus.ReviewedUnsure:   return [128, 128, 128,   0]; //gray
                case AbandonedVehicleStatus.ReviewedVerified: return [255,   0,   0, 255]; //red
                case AbandonedVehicleStatus.ReviewedRejected: return [0,     0,   0,   0]; //invisible (transparent)
                default:                                      return [255, 255, 255, 255]; //white fallback
            }
        },
        getRadius:        1, // radius in meters
        radiusMinPixels:  3, // minimum size in pixels
        radiusMaxPixels: 50, // maximum size in pixels
        pickable: true
    } );
    
    const verifiedVehicles = vehicles.filter( v =>
        v.status.status === AbandonedVehicleStatus.ReviewedVerified
    );

    // Red heatmap for verified
    const verifiedHeatmap = new HeatmapLayer( {
        id:           "verified-heatmap",
        data:         verifiedVehicles,
        aggregation:  "SUM",
        getPosition:  ( d: AbandonedVehicle ) => {
            return pixelToLatLng( d.x, d.y, imageB )
        },
        getWeight:      1,
        radiusPixels:  25,
    } );

    return <div id="list-map-page">
        <div className="p-1">
            This side allows us to add some extra data or explanations
        </div>
        <div style={{ width: '100%', height: '90vh', position: 'relative' }}>
            <DeckGL
                initialViewState={{
                    longitude:  -75.1652,
                    latitude:    39.9526,
                    zoom:        11,
                    pitch:       0,
                    bearing:     0
                }}
                controller={true}
                layers={[h3Layer, verifiedHeatmap]}
                getTooltip={( { object } ) => {
                    if( !object ) {
                        return null;
                    }
                    const normalized = ( ( object.count - min ) / ( max - min ) ).toFixed( 2 );
                    return `${normalized} (${object.count})`;
                }}>
                <Map
                    mapStyle="mapbox://styles/mapbox/light-v9"
                    mapboxAccessToken="pk.eyJ1IjoiZGh5ajc3NyIsImEiOiJjbWFwdGswdjkwMGZyMm1vZDVjejUyOGRjIn0.h0Eb4kX2BTLxou4Eu50pCA" />
            </DeckGL>
        </div>
    </div>;    
}